import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";

import type { Practice } from "../../../@lib/types";
import { Container, Button, Stack, Paper, Typography } from "@mui/material";
import {
  CTAContainer,
  CardAll,
  ConditionalRenderer,
  TabsFilters,
  useLanguageTranslator,
  Header,
} from "../../../components";

import { useState, useCallback } from "react";
import { useAuthSession } from "../../../components/AuthSessionProvider";
import { useMutation, gql } from "@apollo/client";
import { useNotify } from "../../../components/@common/NotificationsProvider";
import { usePrivatePracticesCall } from "../../../hooks/usePrivatePracticesCall";

const deletePracticeMutationDocument = gql`
  mutation DeletePractice($practiceInput: PracticeDeleteInput!) {
    practiceDelete(input: $practiceInput) {
      practice {
        id
      }
    }
  }
`;

const PrivatePracticesList: React.FC = () => {
  const [inputValue, setInputValue] = useState("");
  const [limit, setLimit] = useState(8);
  const [currTabIndex, setCurrTabIndex] = useState(0);

  const notify = useNotify();
  const { user } = useAuthSession() as any;
  const isLoggedIn = !!user;
  const { translator } = useLanguageTranslator();

  const [deletePractice, { loading: deletingPractice }] = useMutation(
    deletePracticeMutationDocument,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );
  const { practices, dist, loading, metadata, refetch }: any =
    usePrivatePracticesCall(user, currTabIndex, { limit: limit });

  function onClickHandler() {
    const scrollPosition = window.scrollY;
    setLimit(limit + 8);

    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  }

  /**
   * handleDeletePractice()
   */
  const handleDeletePractice = useCallback(
    async (practice: Practice) => {
      if (
        await notify.confirm(
          `Deleting practice "${practice.title}". This cannot be undone.`
        )
      ) {
        try {
          await deletePractice({
            variables: {
              practiceInput: {
                id: practice.id,
              },
            },
          });

          notify.success(`Practice "${practice.title}" deleted successfuly!`);
          refetch();
        } catch (err) {
          notify.error(err as Error);
        }
      }
    },
    [deletePractice, notify]
  );

  const privateTab = [
    {
      value: "all",
      count: metadata?.totalCount,
      title: translator("All"),
    },
    {
      value: "approved",
      count: dist.approved.length,
      title: translator("status.Approved"),
    },
    {
      value: "pending",
      count: dist.pending.length,
      title: translator("status.Pending"),
    },
    {
      value: "rejected",
      count: dist.rejected.length,
      title: translator("status.Rejected"),
    },
  ];

  const hasMore =
    limit === metadata?.totalCount ||
    limit > metadata?.totalCount ||
    !metadata?.totalCount;

  const filteredPracticesList = useMemo(() => {
    if (!inputValue) return practices || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return practices?.filter((item: any) =>
      item?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, practices]);

  return (
    <div>
      <Header
        title={translator("Practices")}
        subtitle=""
        setInputValue={setInputValue}
        inputValue={inputValue}
      />

      <Container sx={{ my: 3 }}>
        <TabsFilters
          dist={dist}
          currTab={currTabIndex}
          setCurrTab={setCurrTabIndex}
          listItems={privateTab}
          heading={`${translator("List of")} ${translator("Practices")}`}
          isAllShow={true}
        />
        <Stack gap={2}>
          {filteredPracticesList?.map((p: any, i: any) => (
            <CardAll
              key={`private-practice-list-card-${i}`}
              p={p}
              loading={loading}
              isLoggedIn={isLoggedIn}
              editPath={`/practices/${p?.id}/edit`}
              handleDelete={handleDeletePractice}
              path={`/practices/${p.id}`}
            />
          ))}
        </Stack>
      </Container>

      <ConditionalRenderer condition={!hasMore}>
        <Container sx={{ my: 3 }}>
          <Paper sx={{ py: 2, display: "flex", justifyContent: "center" }}>
            <Button onClick={onClickHandler}>{translator("Show more")}</Button>
          </Paper>
        </Container>
      </ConditionalRenderer>

      {isLoggedIn && (
        <Container sx={{ my: 3 }}>
          <CTAContainer sx={{ textAlign: "center" }}>
            <Typography variant="subtitle1" mb={2}>
              Add new practices to the platform
            </Typography>
            <Button
              component={RouterLink}
              to="/submission/practices/new"
              sx={{
                px: 6,
                backgroundColor: "#ffffff",
                color: "primary.dark",
                "&:hover": {
                  backgroundColor: "primary.light",
                },
              }}
            >
              {translator("generic.CREATE")}
            </Button>
          </CTAContainer>
        </Container>
      )}
    </div>
  );
};

export { PrivatePracticesList };
